import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiBookmark, FiEdit2, FiEye, FiEyeOff, FiHeart, FiLock, FiMessageCircle, FiMoreHorizontal,
  FiRadio, FiShare2, FiShoppingBag, FiSlash, FiTrash2, FiUsers,
} from 'react-icons/fi';
import { cDeletePost, cToggleLike, cToggleSave } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import FollowButton from './FollowButton';
import ImageGallery from './ImageGallery';
import AudioPlayer from './AudioPlayer';
import Linkify from './Linkify';
import PostVideo from './PostVideo';
import UploadedVideo from './UploadedVideo';
import PostBlocks from './PostBlocks';
import ShareMenu from './ShareMenu';
import UserAvatar from './UserAvatar';
import UserBadges from './UserBadges';
import { NAVY, BLUE, compactNumber, timeAgo } from './brand';
import PostComposer from './PostComposer';

/** One community article (feed, profile, post detail). */
export default function PostCard({ post, onChanged, onDeleted, commentsOpen = false }) {
  const { t, lang } = useLanguage();
  const { user, requireAuth } = useCommunity();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(!!post.liked);
  const [likes, setLikes] = useState(post.like_count || 0);
  const [shares, setShares] = useState(post.share_count || 0);
  const [saved, setSaved] = useState(!!post.saved);
  const [saves, setSaves] = useState(post.save_count || 0);
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);

  const like = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireAuth(t('loginRequiredDesc'))) return;
    if (busy) return;
    setBusy(true);
    const before = { liked, likes };
    setLiked(!liked);
    setLikes(likes + (liked ? -1 : 1));
    try {
      const res = await cToggleLike(post.id);
      setLiked(res.liked);
      setLikes(res.like_count);
      if (onChanged) onChanged({ ...post, liked: res.liked, like_count: res.like_count });
    } catch (err) {
      setLiked(before.liked);
      setLikes(before.likes);
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  /** ★ Favourite — save the article to My profile → Saved. */
  const save = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireAuth(t('loginRequiredDesc'))) return;
    if (busy) return;
    setBusy(true);
    const before = { saved, saves };
    setSaved(!saved);
    setSaves(saves + (saved ? -1 : 1));
    try {
      const res = await cToggleSave(post.id);
      setSaved(res.saved);
      setSaves(res.save_count);
      if (onChanged) onChanged({ ...post, saved: res.saved, save_count: res.save_count });
      toast(res.saved ? t('savedToFavorites') : t('removedFromFavorites'),
            { icon: res.saved ? '⭐' : '☆', id: 'fav-toast' });
    } catch (err) {
      setSaved(before.saved);
      setSaves(before.saves);
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t('deletePostConfirm'))) return;
    try {
      await cDeletePost(post.id);
      toast.success(t('deletedOk'));
      if (onDeleted) onDeleted(post.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  if (editing) {
    return (
      <PostComposer
        initial={post}
        onCancel={() => setEditing(false)}
        onSaved={(updated) => { setEditing(false); if (onChanged) onChanged(updated); }}
      />
    );
  }

  const author = post.author || {};
  const detail = `/post/${post.id}`;
  // An article published “as a channel” (the account switcher): the channel is
  // the author and its page is linked — the member behind it is not shown.
  const isChannel = !!author.channel;
  const channelUrl = `/channel/${author.channel_slug || author.channel_id}`;
  const profileUrl = isChannel ? channelUrl : (author.username ? `/u/${author.username}` : '#');

  return (
    <article
      onClick={() => navigate(detail)}
      className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden anim-fade-up cursor-pointer hover:shadow-md transition"
    >
      {post.is_pinned && (
        <div className="flex items-center gap-1.5 px-4 py-1.5 text-[11px] font-bold text-white" style={{ background: NAVY }}>
          <FiBookmark className="w-3 h-3" /> {t('pinedPost')}
        </div>
      )}

      <div className="p-4">
        <header className="flex items-start gap-3">
          {/* Avatar / author / category / shop links stop the click from opening the card */}
          <span onClick={(e) => e.stopPropagation()}>
            <UserAvatar
              user={author}
              size={44}
              to={isChannel ? channelUrl : (author.username ? `/u/${author.username}` : null)}
            />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={profileUrl}
                onClick={(e) => e.stopPropagation()}
                className="font-bold text-sm hover:underline truncate"
              >
                {author.name || author.username}
              </Link>
              {/* ✓ verified / VIP member (granted in the admin Community → Members tab) */}
              <UserBadges user={author} />
              {/* the article belongs to a channel account */}
              {isChannel && (
                <Link
                  to={channelUrl}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                  style={{ background: `${BLUE}18`, color: BLUE }}
                >
                  <FiRadio className="w-3 h-3" /> {t('channelPostBadge')}
                </Link>
              )}
              {post.category && (
                <Link
                  to={`/?category_id=${post.category.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: `${BLUE}18`, color: BLUE }}
                >
                  {post.category.icon} {lang === 'kh' ? (post.category.name_km || post.category.name) : post.category.name}
                </Link>
              )}
            </div>
            <div className="flex items-center gap-2 text-[11px] text-gray-500 flex-wrap">
              <span>@{author.username}</span>
              <span>·</span>
              <span>{timeAgo(post.created_at, lang)}</span>
              {/* “ជម្រើសនៃការបង្ហោះសារ” — the privacy of this article */}
              {post.visibility === 'friends' && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
                  <FiUsers className="w-3 h-3" /> {t('badgeFriends')}
                </span>
              )}
              {post.visibility === 'only_me' && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
                  <FiLock className="w-3 h-3" /> {t('badgeOnlyMe')}
                </span>
              )}
              {post.allow_comments === false && (
                <span title={t('commentsOff')} className="inline-flex items-center gap-1 text-gray-400">
                  <FiEyeOff className="w-3 h-3" /> {t('turnOffComments')}
                </span>
              )}
              {post.allow_share === false && (
                <span title={t('shareOff')} className="inline-flex items-center gap-1 text-gray-400">
                  <FiSlash className="w-3 h-3" /> {t('turnOffShare')}
                </span>
              )}
              {post.shop && (
                <Link
                  to={`/${post.shop.username}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 font-semibold"
                  style={{ color: NAVY }}
                >
                  <FiShoppingBag className="w-3 h-3" /> {post.shop.shop_name}
                </Link>
              )}
            </div>
          </div>

          {user && user.id !== author.id && !isChannel && (
            <span onClick={(e) => e.stopPropagation()}>
              <FollowButton
                user={{ ...author, is_following: post.following_author, follows_you: author.follows_you }}
              />
            </span>
          )}

          {post.can_manage && (
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setMenu((v) => !v)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Post menu"
              >
                <FiMoreHorizontal className="w-5 h-5" />
              </button>
              {menu && (
                <div className="absolute right-0 z-30 mt-1 w-40 rounded-xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-100 dark:border-gray-800 p-1.5 anim-pop">
                  <button
                    type="button"
                    onClick={() => { setMenu(false); setEditing(true); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <FiEdit2 className="w-4 h-4" /> {t('edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMenu(false); remove(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-gray-800"
                  >
                    <FiTrash2 className="w-4 h-4" /> {t('delete')}
                  </button>
                </div>
              )}
            </div>
          )}
        </header>

        {post.title && (
          <Link
            to={detail}
            onClick={(e) => e.stopPropagation()}
            className="block mt-3 font-extrabold text-lg leading-snug hover:underline"
          >
            {post.title}
          </Link>
        )}

        {/* A text-only Status post can carry its own coloured background */}
        {post.bg && post.description && !post.images?.length && !post.video && !post.video_url && (
          <Link to={detail} onClick={(e) => e.stopPropagation()} className="block mt-3">
            <span
              className="block rounded-2xl px-5 py-8 text-center"
              style={{ background: post.bg }}
            >
              <span className="block text-white font-extrabold text-lg sm:text-xl leading-snug whitespace-pre-wrap break-words">
                {post.description}
              </span>
            </span>
          </Link>
        )}

        {post.description && !(post.bg && !post.images?.length && !post.video && !post.video_url) && (
          <Linkify
            text={post.description}
            className="mt-1.5 text-sm text-gray-800 dark:text-gray-100"
            clamp={commentsOpen ? 0 : 7}
            onMore={commentsOpen ? null : () => navigate(detail)}
            moreLabel={`… ${t('readMore')}`}
          />
        )}

        {post.images?.length > 0 && (
          <div className="mt-3" onClick={(e) => e.stopPropagation()}>
            <ImageGallery images={post.images} alt={post.title} />
          </div>
        )}

        {post.video ? (
          <div className="mt-3" onClick={(e) => e.stopPropagation()}>
            <UploadedVideo post={post} autoPlayOnView={!commentsOpen} />
          </div>
        ) : post.video_url ? (
          <div className="mt-3" onClick={(e) => e.stopPropagation()}>
            <PostVideo url={post.video_url} title={post.title} />
          </div>
        ) : null}

        {post.audio && (
          <div onClick={(e) => e.stopPropagation()}>
            <AudioPlayer
              src={post.audio}
              title={post.audio_title}
              cover={post.images?.[0] || ''}
              round
              size={112}
            />
          </div>
        )}

        {/* Long-article sections: the feed card only hints — the full article
            (every section) is shown on the post detail page. */}
        <div onClick={(e) => e.stopPropagation()}>
          <PostBlocks blocks={post.blocks} alt={post.title} hint={!commentsOpen} />
        </div>

        {(likes > 0 || shares > 0 || post.comment_count > 0 || post.view_count > 0) && (
          <div className="mt-3 flex items-center justify-between text-[11px] text-gray-500">
            <span>
              {t('likes')} {compactNumber(likes)} · {t('comments')} {compactNumber(post.comment_count || 0)}
            </span>
            <span className="inline-flex items-center gap-3">
              {shares > 0 && <span>{t('shares')} {compactNumber(shares)}</span>}
              {post.view_count > 0 && (
                <span className="inline-flex items-center gap-1">
                  <FiEye className="w-3.5 h-3.5" /> {compactNumber(post.view_count)}
                </span>
              )}
            </span>
          </div>
        )}

        <footer className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            data-sfx="success"
            onClick={like}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-semibold text-xs sm:text-sm transition ${
              liked ? 'bg-red-50 dark:bg-gray-800' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            style={{ color: liked ? '#e11d48' : undefined }}
          >
            <FiHeart className="w-4 h-4" fill={liked ? '#e11d48' : 'none'} /> {t('like')}
            {likes > 0 && <span className="text-gray-400">{compactNumber(likes)}</span>}
          </button>

          {post.allow_comments === false ? (
            <span
              title={t('commentsOff')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-semibold text-xs sm:text-sm text-gray-400 cursor-default"
            >
              <FiMessageCircle className="w-4 h-4" /> {t('commentsOff')}
            </span>
          ) : (
            <Link
              to={detail}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-semibold text-xs sm:text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <FiMessageCircle className="w-4 h-4" /> {t('comment')}
              {post.comment_count > 0 && <span className="text-gray-400">{compactNumber(post.comment_count)}</span>}
            </Link>
          )}

          <button
            type="button"
            data-sfx="success"
            onClick={save}
            title={saved ? t('removedFromFavorites') : t('savePost')}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-semibold text-xs sm:text-sm transition ${
              saved ? 'bg-amber-50 dark:bg-gray-800' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            style={{ color: saved ? '#d97706' : undefined }}
          >
            <FiBookmark className="w-4 h-4" fill={saved ? '#d97706' : 'none'} />
            <span className="hidden sm:inline">{saved ? t('savedPost') : t('savePost')}</span>
            {saves > 0 && <span className="text-gray-400">{compactNumber(saves)}</span>}
          </button>

          {post.allow_share === false ? (
            <span
              title={t('shareOff')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-semibold text-xs sm:text-sm text-gray-400 cursor-default"
            >
              <FiShare2 className="w-4 h-4" /> <FiSlash className="w-3 h-3" />
              <span className="hidden sm:inline">{t('turnOffShare')}</span>
            </span>
          ) : (
            <ShareMenu
              post={post}
              onChange={(res) => { setShares(res.share_count); if (onChanged) onChanged({ ...post, share_count: res.share_count }); }}
            />
          )}
        </footer>
      </div>
    </article>
  );
}
